import { useState, useEffect, useMemo } from 'react'
import { Rnd } from 'react-rnd'

import { CELL_WIDTH, CELL_WIDTH_5MIN } from 'components/common/constants'

import stylesDefault from './RndItem.module.scss'
import styles5min from './RndItem5min.module.scss'

import type { ResizeDirection } from 're-resizable'
import type { ReactNode } from 'react'
import type { DraggableEvent } from 'react-draggable'
import type { DraggableData, Position, ResizableDelta } from 'react-rnd'

export type OnChangeProps = {
  x: number
  width: number
}

type Props = {
  children: ReactNode
  onChange: (p: OnChangeProps) => void
  activeRange: Array<[number, number]>
  initialX?: number
  initialWidth?: number
  color?: string
  invertedColor?: boolean
  disabled?: boolean
  isTeam?: boolean
  is5min?: boolean
}

export const RndItem = ({
  children,
  onChange,
  activeRange,
  initialX = 0,
  initialWidth = 1,
  color = 'secondary-dark',
  invertedColor = false,
  disabled = false,
  isTeam = false,
  is5min = false,
}: Props) => {
  const [item, setItem] = useState<OnChangeProps>({ x: initialX, width: initialWidth })
  const [ref, setRef] = useState<Rnd | null>(null)

  const currentRange = useMemo(() => {
    const start = item.x
    const end = item.width + item.x - 1
    return activeRange.find(range => (range[0] <= start && start <= range[1]) || (range[0] <= end && end <= range[1]))
  }, [activeRange, item])

  const baseActiveRange = (start: number, end: number) => {
    // 右端or左端が含まれるactiveRange
    const rangeA = activeRange.find(
      range => (range[0] <= start && start <= range[1]) || (range[0] <= end && end <= range[1])
    )
    // 右端or左端の間にactiveRangeがある場合
    const rangeB = activeRange.find(range => start < range[0] && range[1] <= end)
    // 左右端がどのrangeにも含まれない場合
    const rangeC = activeRange
      .filter(range => (start < item.x ? end < range[0] : range[1] < start))
      .sort((a, b) => Math.abs(a[1] - start) - Math.abs(b[1] - start))

    return rangeA || rangeB || rangeC[0]
  }

  const cellWidth = useMemo(() => (is5min ? CELL_WIDTH_5MIN / 3 : CELL_WIDTH), [is5min])
  const styles = useMemo(() => (is5min ? styles5min : stylesDefault), [is5min])

  const handleResizeStop = (
    _e: MouseEvent | TouchEvent,
    _dir: ResizeDirection,
    element: HTMLElement,
    _delta: ResizableDelta,
    position: Position
  ) => {
    const x = Math.round(position.x) / cellWidth
    const width = parseInt(element.style.width) / cellWidth
    const end = x + width

    // activeRangeは開始位置or終了位置で入力可能域を判定する
    const base = currentRange ?? baseActiveRange(x, end)
    if (x < base[0]) {
      const resize = width - (base[0] - x)
      setItem({ x: base[0], width: resize })
      onChange({ x: base[0], width: resize })
      ref?.updatePosition({ x: cellWidth * base[0], y: 0 })
      ref?.updateSize({ width: cellWidth * resize, height: '100%' })
    } else if (x + width > base[1] + 1) {
      const resize = x === base[0] ? base[1] - base[0] : base[1] - x
      setItem({ x, width: resize + 1 })
      onChange({ x, width: resize + 1 })
      ref?.updatePosition({ x: cellWidth * x, y: 0 })
      ref?.updateSize({ width: cellWidth * (resize + 1), height: '100%' })
    } else {
      setItem({ x, width })
      onChange({ x, width })
    }
  }

  const handleDragStop = (_e: DraggableEvent, data: DraggableData) => {
    const startX = data.lastX / cellWidth
    const endX = item.width + startX - 1

    // activeRangeは開始位置or終了位置で入力可能域を判定する
    const base = baseActiveRange(startX, endX)
    const baseWidth = Math.min(base[1] - base[0] + 1, item.width)

    if (startX < base[0]) {
      const reposition = base[0]
      const width = base[1] < endX || endX < base[0] ? baseWidth : endX - reposition + 1
      setItem({ x: reposition, width })
      onChange({ x: reposition, width })
      ref?.updatePosition({ x: cellWidth * reposition, y: 0 })
      ref?.updateSize({ width: cellWidth * width, height: '100%' })
    } else if (base[1] < endX) {
      const reposition = base[1] < startX ? base[1] - baseWidth + 1 : startX
      const width = base[1] < startX ? baseWidth : base[1] - reposition + 1
      setItem({ x: reposition, width })
      onChange({ x: reposition, width })
      ref?.updatePosition({ x: cellWidth * reposition, y: 0 })
      ref?.updateSize({ width: cellWidth * width, height: '100%' })
    } else {
      setItem({ ...item, x: startX })
      onChange({ ...item, x: startX })
    }
  }

  useEffect(() => {
    setItem({ x: initialX, width: initialWidth })
    ref?.updatePosition({ x: cellWidth * initialX, y: 0 })
    ref?.updateSize({ width: cellWidth * initialWidth, height: '100%' })
  }, [initialX, initialWidth, ref, cellWidth])

  const itemStyle = {
    color: invertedColor ? `var(--bs-${color})` : '#ffffff',
    backgroundColor: invertedColor ? '#ffffff' : `var(--bs-${color})`,
    boxShadow: invertedColor ? '3px 2.5px 5px var(--bs-secondary-middle)' : '',
    border: `2px var(--bs-${color}) solid`,
    opacity: disabled ? 0.4 : 0.8,
  }

  const isMinimumWidthItem = useMemo(() => {
    return item.width === 1
  }, [item])

  const enableResize = useMemo(() => {
    const canResize = !disabled && !isTeam
    // 5分間隔の場合、さらにアイテムが10分以上の長さである必要がある
    return is5min ? canResize && !isMinimumWidthItem : canResize
  }, [disabled, is5min, isMinimumWidthItem, isTeam])

  const itemContainerStyle = useMemo(() => {
    if (is5min) {
      const paddingStyle = !isMinimumWidthItem ? styles.itemContainerPadding : ''
      return `${styles.itemContainerBorder} ${paddingStyle}`
    }

    return styles.itemContainer
  }, [is5min, isMinimumWidthItem, styles])

  return (
    <Rnd
      className={itemContainerStyle}
      style={itemStyle}
      ref={rnd => setRef(rnd)}
      default={{
        x: cellWidth * initialX,
        y: 0,
        width: cellWidth * initialWidth,
        height: '100%',
      }}
      dragAxis="x"
      disableDragging={disabled || isTeam}
      enableResizing={{
        top: false,
        right: enableResize,
        bottom: false,
        left: enableResize,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      bounds="parent"
      resizeGrid={[cellWidth, 0]}
      dragGrid={[cellWidth, 0]}
      minWidth={cellWidth}
      onResizeStop={handleResizeStop}
      onDragStop={handleDragStop}
    >
      <div className={styles.innerBox}>{children}</div>
    </Rnd>
  )
}
